/**
 * Centralized Razorpay Payment Helper
 * Used across the application for consistent payment handling
 */

export type RazorpayCurrency = "INR";

export interface RazorPayBasicOptions {
  amount: number;
  name: string;
  order_id: string;
  description: string;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  features?: {
    wallet_on_checkout?: boolean;
  };
}

export interface RazorPayInitiateConfig extends RazorPayBasicOptions {
  onSuccess: (response: RazorpayResponse) => void | Promise<void>;
  onFailure?: (error: any) => void;
  onDismiss?: () => void;
}

export interface RazorpayConfig extends RazorPayBasicOptions {
  key: string;
  currency: RazorpayCurrency;
  handler: (config: RazorpayResponse) => void;
  modal: {
    ondismiss: () => void;
  };
}

export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

/**
 * Initialize and open Razorpay payment interface
 * @param config Payment configuration
 */
export const initiateRazorpayPayment = (
  config: RazorPayInitiateConfig
): Promise<RazorpayResponse> => {
  return new Promise((resolve, reject) => {
    // Validate required parameters
    if (!config.order_id || !config.amount) {
      const error = new Error("Missing required payment parameters");
      config.onFailure?.(error);
      reject(error);
      return;
    }

    // Convert amount to paise
    const amountInPaise = Math.round(config.amount * 100);

    if (amountInPaise <= 0) {
      const error = new Error("Invalid amount");
      config.onFailure?.(error);
      reject(error);
      return;
    }

    // Check if Razorpay is loaded
    if (!(window as any).Razorpay) {
      const error = new Error("Razorpay script not loaded");
      config.onFailure?.(error);
      reject(error);
      return;
    }

    const options: RazorpayConfig = {
      key: process.env.REACT_APP_RAZOR_PAY_KEY_ID || "",
      amount: amountInPaise,
      currency: "INR",
      name: config.name || "GoodFlip",
      description: config.description || "Payment",
      order_id: config.order_id,
      prefill: config.prefill,
      features: config.features,
      handler: async (response: RazorpayResponse) => {
        try {
          await config.onSuccess(response);
          resolve(response);
        } catch (error) {
          config.onFailure?.(error);
          reject(error);
        }
      },
      modal: {
        ondismiss: () => {
          const dismissError = new Error("Payment cancelled by user");
          config.onDismiss?.();
          config.onFailure?.(dismissError);
          reject(dismissError);
        },
      },
    };

    const rzp = new (window as any).Razorpay(options);

    // Handle payment failures
    rzp.on("payment.failed", (response: any) => {
      console.error("Payment failed", response);
      const error = new Error(response.error?.description || "Payment failed");
      config.onFailure?.(error);
      reject(error);
    });

    // Open payment interface
    rzp.open();
  });
};

/**
 * Helper to format amount for display
 * @param amount Amount in rupees
 * @returns Formatted amount string with currency symbol
 */
export const formatAmount = (amount: number): string => {
  return `₹${amount.toLocaleString("en-IN")}`;
};

/**
 * Helper to validate Razorpay response
 * @param response Razorpay response object
 * @returns boolean indicating if response is valid
 */
export const validateRazorpayResponse = (
  response: any
): response is RazorpayResponse => {
  return !!(
    response &&
    response.razorpay_payment_id &&
    response.razorpay_order_id &&
    response.razorpay_signature
  );
};
